<template>
	<section class="payment-plan-page" :aria-label="$t('payment-plans.heading')">
		<div v-if="loading" class="plan-state" role="status">
			<v-progress-circular indeterminate color="primary" size="34"></v-progress-circular>
			<span>{{ $t('payment-plans.loading') }}</span>
		</div>
		<div v-else-if="loadError" class="plan-state" role="alert">
			<portal-icon name="alert-circle" :size="28"></portal-icon>
			<span>{{ $t('payment-plans.load-error') }}</span>
			<v-btn text color="primary" @click="$emit('refresh')">{{ $t('payment-plans.try-again') }}</v-btn>
		</div>
		<div v-else-if="!plans.length" class="plan-state">
			<portal-icon name="calendar-check" :size="30"></portal-icon>
			<strong>{{ $t('payment-plans.no-active') }}</strong>
			<span>{{ $t('payment-plans.no-active-detail') }}</span>
		</div>
		<div v-else class="plan-list">
			<details v-for="plan in plans" :key="plan.id" class="plan-card" :open="plan.id === highlightedPlanId">
				<summary>
					<div class="plan-heading">
						<div>
							<strong>{{ plan.name || $t('payment-plans.untitled') }}</strong>
							<portal-status-pill
								:color="plan.state === 'REVIEWING' ? 'var(--v-warning-base)' : 'var(--v-success-base)'"
								:background="plan.state === 'REVIEWING' ? 'var(--v-warning_10-base)' : 'var(--v-success_10-base)'"
							>
								{{ $t(`payment-plans.states.${plan.state.toLowerCase()}`) }}
							</portal-status-pill>
						</div>
						<span v-if="next(plan)" class="plan-next">
							{{ $t('payment-plans.next') }}: {{ next(plan).label }} · {{ trigger(plan, next(plan)) }}
						</span>
					</div>
					<div class="plan-balance">
						<strong>{{ money(plan.remaining, plan.currency) }}</strong>
						<span>{{ $t('payment-plans.remaining') }}</span>
					</div>
					<portal-icon name="chevron-down" :size="20"></portal-icon>
				</summary>

				<div class="plan-content">
					<div class="plan-metrics">
						<div><span>{{ $t('payment-plans.plan-total') }}</span><strong>{{ money(plan.planTotal, plan.currency) }}</strong></div>
						<div><span>{{ $t('payment-plans.open-invoices') }}</span><strong>{{ money(plan.openInvoiceBalance, plan.currency) }}</strong></div>
						<div><span>{{ $t('payment-plans.scheduled-later') }}</span><strong>{{ money(plan.scheduledLater, plan.currency) }}</strong></div>
					</div>
					<p class="collection-note">
						<portal-icon :name="plan.autoCharge ? 'credit-card' : 'send'" :size="17"></portal-icon>
						<span>{{ collectionLabel(plan) }} {{ terms(plan) }}</span>
					</p>

					<ol class="plan-timeline">
						<li v-for="step in plan.steps" :key="step.id" :class="step.state.toLowerCase()">
							<span class="step-mark" aria-hidden="true"><portal-icon :name="stepIcon(step)" :size="15"></portal-icon></span>
							<div class="step-copy">
								<div class="step-title">
									<strong>{{ step.label || $t('payment-plans.payment') }}</strong>
									<span>{{ money(step.amount, plan.currency) }}</span>
								</div>
								<small>{{ trigger(plan, step) }}</small>
								<button v-if="step.invoiceId" type="button" class="invoice-link" @click="openInvoice(step.invoiceId)">
									{{ $t('payment-plans.view-invoice', { number: step.invoiceNumberFormatted }) }}
								</button>
							</div>
							<span class="step-state">{{ $t(`payment-plans.step-states.${step.state.toLowerCase()}`) }}</span>
						</li>
					</ol>
				</div>
			</details>
		</div>
	</section>
</template>

<script>
	import InvoiceDetails from '@/modules/invoices/InvoiceDetails';
	import { localizedDate, nextStep, paymentTermsLabel, triggerLabel } from '@/modules/invoices/paymentPlanDisplay';

	export default {
		name: 'PaymentPlanList',
		props: {
			plans: { type: Array, default: () => [] },
			loading: { type: Boolean, default: false },
			loadError: { type: Boolean, default: false },
			highlightedPlanId: { type: String, default: null },
		},
		methods: {
			next: nextStep,
			money(amount, currency) {
				return this.$formatters.dollars(amount || 0, true, true, currency);
			},
			trigger(plan, step) {
				if (step.state === 'PREPARING') return this.$t('payment-plans.preparing-invoice');
				if (['PAID', 'SETTLED'].includes(step.state) && step.invoiceDate) {
					return this.$t('payment-plans.invoiced-date', {
						date: localizedDate(this.$i18n.locale, step.invoiceDate),
					});
				}
				return triggerLabel(this.$i18n, step);
			},
			terms(plan) {
				return paymentTermsLabel(this.$i18n, plan.paymentDays);
			},
			collectionLabel(plan) {
				if (plan.autoCharge) return this.$t('payment-plans.auto-charge');
				if (plan.autoSend) return this.$t('payment-plans.auto-send');
				return this.$t('payment-plans.review-send');
			},
			stepIcon(step) {
				if (step.state === 'PAID') return 'check';
				if (step.state === 'SETTLED') return 'check-circle';
				if (step.state === 'OPEN') return 'receipt-text';
				if (step.state === 'PREPARING') return 'clock';
				return 'calendar';
			},
			openInvoice(invoiceId) {
				this.$store.state.globalModalController.openModal(
					InvoiceDetails,
					{ invoiceId },
					true,
					false,
					'document-lightbox'
				);
			},
		},
	};
</script>

<style scoped lang="scss">
	.payment-plan-page {
		padding: 16px;
	}
	.plan-list {
		max-width: 980px;
		display: grid;
		gap: 14px;
		margin: 0 auto;
	}
	.plan-card {
		overflow: hidden;
		border: 1px solid var(--portal-border);
		border-radius: var(--portal-radius-md);
		background: var(--portal-surface);
		text-align: left;
	}
	.plan-card summary {
		min-height: 92px;
		display: grid;
		grid-template-columns: minmax(0, 1fr) auto 24px;
		align-items: center;
		gap: 22px;
		padding: 18px 20px;
		color: var(--portal-text);
		cursor: pointer;
		list-style: none;
	}
	.plan-card summary::-webkit-details-marker {
		display: none;
	}
	.plan-card[open] summary {
		border-bottom: 1px solid var(--portal-border);
	}
	.plan-card[open] summary > .portal-icon {
		transform: rotate(180deg);
	}
	.plan-heading,
	.plan-heading > div,
	.plan-balance {
		display: flex;
	}
	.plan-heading,
	.plan-balance {
		min-width: 0;
		flex-direction: column;
	}
	.plan-heading {
		gap: 7px;
	}
	.plan-heading > div {
		align-items: center;
		gap: 10px;
	}
	.plan-heading > div > strong {
		overflow: hidden;
		font-size: 16px;
		font-weight: 600;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.plan-next,
	.plan-balance span,
	.plan-metrics span,
	.step-copy small {
		color: var(--portal-muted);
		font-size: 12px;
	}
	.plan-next {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.plan-balance {
		align-items: flex-end;
	}
	.plan-balance strong {
		font-size: 20px;
		font-weight: 600;
	}
	.plan-content {
		padding: 20px;
	}
	.plan-metrics {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 12px;
	}
	.plan-metrics > div {
		display: grid;
		gap: 4px;
		padding: 13px 14px;
		border-radius: var(--brand-surface-radius, 12px);
		background: var(--v-gray_10-base);
	}
	.plan-metrics strong {
		font-size: 16px;
		font-weight: 600;
	}
	.collection-note {
		display: flex;
		align-items: flex-start;
		gap: 9px;
		margin: 16px 0;
		padding: 12px 14px;
		border-radius: var(--brand-surface-radius, 12px);
		background: var(--v-blue_5-base);
		color: var(--portal-text);
		font-size: 13px;
	}
	.collection-note .portal-icon {
		flex: 0 0 auto;
		color: var(--v-primary-base);
	}
	.plan-timeline {
		margin: 0;
		padding: 0;
		list-style: none;
	}
	.plan-timeline li {
		position: relative;
		display: grid;
		grid-template-columns: 28px minmax(0, 1fr) auto;
		align-items: start;
		gap: 12px;
		padding: 12px 0;
		border-bottom: 1px solid var(--portal-border);
	}
	.plan-timeline li:last-child {
		border-bottom: 0;
	}
	.step-mark {
		width: 28px;
		height: 28px;
		display: grid;
		place-items: center;
		border-radius: 50%;
		background: var(--v-gray_20-base);
		color: var(--portal-muted);
	}
	li.open .step-mark {
		background: var(--v-warning_10-base);
		color: var(--v-warning-base);
	}
	li.paid .step-mark,
	li.settled .step-mark {
		background: var(--v-success_10-base);
		color: var(--v-success-base);
	}
	.step-copy {
		min-width: 0;
		display: grid;
		gap: 4px;
	}
	.step-title {
		display: flex;
		justify-content: space-between;
		gap: 12px;
	}
	.step-title strong,
	.step-title span {
		font-size: 14px;
		font-weight: 600;
	}
	.invoice-link {
		justify-self: start;
		padding: 0;
		border: 0;
		background: transparent;
		color: var(--v-primary-base);
		font-size: 12px;
		font-weight: 600;
		cursor: pointer;
	}
	.step-state {
		padding-top: 4px;
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 600;
	}
	.plan-state {
		min-height: 260px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		gap: 10px;
		color: var(--portal-muted);
		text-align: center;
	}
	@media (max-width: 699px) {
		.payment-plan-page {
			padding: 12px;
		}
		.plan-card summary {
			grid-template-columns: minmax(0, 1fr) 20px;
			gap: 12px;
			padding: 16px;
		}
		.plan-balance {
			grid-column: 1;
			grid-row: 2;
			align-items: flex-start;
		}
		.plan-card summary > .portal-icon {
			grid-column: 2;
			grid-row: 1 / span 2;
		}
		.plan-content {
			padding: 16px;
		}
		.plan-metrics {
			grid-template-columns: 1fr;
		}
		.plan-timeline li {
			grid-template-columns: 28px minmax(0, 1fr);
		}
		.step-state {
			grid-column: 2;
			padding: 0;
		}
	}
</style>
